"use client"

import {
  memo,
  useCallback,
  useMemo,
} from "react"

import {
  ListMusic,
  ChevronDown,
} from "lucide-react"

import { useSelector } from "react-redux"

import { useMusicContext } from "@/context/musicContext"

import QueueItem from "./components/QueueItem"

import {
  getQueueSongId,
  type QueueSong,
} from "./components/queue-utils"

type Props = {
  songs?: QueueSong[]
  className?: string
  maxHeight?: number
}

function QueueCard({
  songs = [],
  className = "",
  maxHeight = 300,
}: Props) {

  const {
    currentSong,
    currentTrackData,
  } = useMusicContext()

  /*
   * =========================================================
   * REDUX
   * =========================================================
   */

  const isPlaying = useSelector(
    (state: any) =>
      state.isPlaying,
  )

  /*
   * =========================================================
   * CURRENT TRACK
   * =========================================================
   */

  const currentSongLink = useSelector(
    (state: any) =>
      state.trackData?.track ?? null,
  )

  /*
   * =========================================================
   * CURRENT INDEX
   * =========================================================
   */

  const currentIndex = useMemo(() => {
    const index =
      currentTrackData?.trackKey?.[1]

    return typeof index === "number"
      ? index
      : null
  }, [
    currentTrackData,
  ])

  /*
   * =========================================================
   * CURRENT SONG MATCH
   * =========================================================
   */

  const isCurrentSong = useCallback(
    (
      song: QueueSong,
      index: number,
    ) => {

      /*
       * Primera comprobación:
       * índice del track.
       */

      if (
        currentIndex !== null &&
        currentIndex === index &&
        currentSong?.songName ===
          song.songName
      ) {
        return isPlaying === true
      }

      /*
       * Fallback:
       * link del audio.
       */

      if (
        currentSongLink &&
        song.link
      ) {
        return (
          song.link ===
            currentSongLink &&
          isPlaying === true
        )
      }

      return false
    },
    [
      currentIndex,
      currentSong,
      currentSongLink,
      isPlaying,
    ],
  )

  /*
   * =========================================================
   * VISIBLE SONGS
   * =========================================================
   */

  const visibleSongs = useMemo(() => {
    return songs.filter(Boolean)
  }, [
    songs,
  ])

  /*
   * =========================================================
   * EMPTY
   * =========================================================
   */

  if (!visibleSongs.length) {
    return null
  }

  /*
   * =========================================================
   * RENDER
   * =========================================================
   */

  return (
    <section
      className={`
        relative
        w-full
        overflow-hidden
        rounded-[22px]
        border
        border-white/[0.06]
        //bg-[var(--soft-bg)]
        bg-[var(--color-sombra-min)]
        ${className}
      `}
    >

      {/* ===================================================
          AMBIENT
      ==================================================== */}

      <div
        className="
          pointer-events-none
          absolute
          -right-20
          -top-24
          h-48
          w-48
          rounded-full
          bg-[var(--primary-color)]/[0.025]
          blur-[75px]
        "
      />

      <div
        className="
          pointer-events-none
          absolute
          -bottom-24
          -left-20
          h-40
          w-40
          rounded-full
          bg-white/[0.015]
          blur-[60px]
        "
      />

      {/* ===================================================
          CONTENT
      ==================================================== */}

      <div className="relative z-10">

        {/* =================================================
            HEADER
        ================================================= */}

        <header
          className="
            flex
            items-center
            justify-between
            gap-2
            px-3
            pb-2
            pt-3
          "
        >

          <div className="flex items-center gap-2.5">

            <div
              className="
                flex
                h-8
                w-8
                items-center
                justify-center
                rounded-lg
                border
                border-white/[0.06]
                bg-white/[0.035]
              "
            >
              <ListMusic
                size={14}
                strokeWidth={2}
                className="text-white/55"
              />
            </div>

            <div className="min-w-0">

              <p
                className="
                  text-[9px]
                  font-semibold
                  uppercase
                  tracking-[0.2em]
                  text-white/70
                "
              >
                A continuación
              </p>

              <p
                className="
                  mt-0.5
                  text-[10px]
                  text-white/30
                "
              >
                Próximas reproducciones
              </p>

            </div>
          </div>

          {/* COUNT */}

          <div
            className="
              flex
              items-center
              gap-1.5
              rounded-full
              border
              border-white/[0.05]
              bg-white/[0.025]
              px-2
              py-1
            "
          >
            <span
              className="
                text-[8px]
                font-medium
                uppercase
                tracking-[0.12em]
                text-white/40
              "
            >
              {visibleSongs.length}
            </span>

            <ChevronDown
              size={10}
              className="text-white/20"
            />
          </div>
        </header>

        {/* =================================================
            DIVIDER
        ================================================= */}

        <div className="mx-4 h-px bg-white/[0.04]" />

        {/* =================================================
            QUEUE
        ================================================= */}

        <div
          className="
            px-2
            py-2
          "
        >
          <div
            className="
              overflow-y-auto
              overscroll-contain
              scrollbar-none
            "
            style={{
              maxHeight,
            }}
          >

            <div className="space-y-0.5">

              {visibleSongs.map(
                (song, index) => {

                  const current =
                    isCurrentSong(
                      song,
                      index,
                    )

                  return (
                    <QueueItem
                      key={getQueueSongId(
                        song,
                        index,
                      )}
                      song={song}
                      index={index}
                      isCurrent={current}
                    />
                  )
                },
              )}

            </div>
          </div>
        </div>

      </div>
    </section>
  )
}

export default memo(QueueCard)